// Lists Manager Modal:
// Create, edit, and delete subscriber lists with member count display
// and cascading deletion confirmation.

import { useCallback, useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { listSave, listDelete, type ListDoc } from '../../lib/crm';

export interface ListsManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  lists: ListDoc[];
}

export default function ListsManagerModal({
  isOpen,
  onClose,
  lists,
}: ListsManagerModalProps) {
  const { t } = useI18n();

  const [editingList, setEditingList] = useState<ListDoc | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(isOpen, panelRef, onClose);

  const confirmRef = useRef<HTMLDivElement>(null);
  const confirmTitleId = useId();
  const closeConfirm = useCallback(() => setDeleteId(null), []);
  useDialogA11y(isOpen && deleteId !== null, confirmRef, closeConfirm);

  if (!isOpen) return null;

  function startEdit(l: ListDoc) {
    setEditingList(l);
    setName(l.name);
    setDescription(l.description || '');
    setError(null);
  }

  function resetForm() {
    setEditingList(null);
    setName('');
    setDescription('');
    setError(null);
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await listSave({
        id: editingList ? editingList.id : undefined,
        name: trimmed,
        description: description.trim() || undefined,
      });
      resetForm();
    } catch (err) {
      setError((err as Error).message || 'Failed to save list');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    setBusy(true);
    setError(null);
    try {
      await listDelete({ id });
      setDeleteId(null);
      if (editingList?.id === id) resetForm();
    } catch (err) {
      setError((err as Error).message || 'Failed to delete list');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h3 id={titleId} className="text-base font-bold text-slate-900">
              {t('subs.lists.title') || 'Manage Lists'}
            </h3>
            <p className="text-xs text-slate-500">
              {t('subs.lists.subtitle') || 'Static subscriber groupings for broadcast targeting and campaigns'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Create / Edit Form */}
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {editingList
                ? t('subs.lists.edit') || 'Edit List'
                : t('subs.lists.create') || 'Create New List'}
            </h4>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {t('subs.lists.name') || 'List Name'}
                </label>
                <input
                  type="text"
                  value={name}
                  maxLength={100}
                  placeholder={t('subs.listNamePlaceholder') || 'e.g. Muscat Retail Customers'}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  {t('subs.lists.description') || 'Description (Optional)'}
                </label>
                <textarea
                  value={description}
                  rows={2}
                  placeholder="Brief note about the audience..."
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
              </div>

              {error && <p className="text-xs font-medium text-red-600">⚠️ {error}</p>}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || !name.trim()}
                  className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? 'Saving...' : editingList ? 'Update List' : 'Save List'}
                </button>
                {editingList && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Existing Lists */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {t('subs.lists.list') || 'Existing Lists'} ({lists.length})
            </h4>

            {lists.length === 0 ? (
              <p className="text-xs text-slate-400 italic py-2">
                No subscriber lists created yet.
              </p>
            ) : (
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
                {lists.map((l) => (
                  <div
                    key={l.id}
                    className="flex items-center justify-between p-3 text-xs hover:bg-slate-50/80"
                  >
                    <div className="min-w-0 flex-1 pe-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800">{l.name}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                          {l.count || 0} subscribers
                        </span>
                      </div>
                      {l.description && (
                        <p className="text-[11px] text-slate-400 truncate mt-0.5">
                          {l.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(l)}
                        className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteId(l.id)}
                        className="rounded px-2 py-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal Overlay */}
        {deleteId && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-slate-900/40 p-4">
            <div
              ref={confirmRef}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby={confirmTitleId}
              className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl space-y-3"
            >
              <h4 id={confirmTitleId} className="text-sm font-bold text-slate-900">Delete List?</h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                Deleting this list will automatically remove it from all matching subscribers. The subscriber records themselves will remain.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setDeleteId(null)}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(deleteId)}
                  disabled={busy}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700"
                >
                  {busy ? 'Deleting...' : 'Delete List'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
